import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categoryInfo, categoryItems } from '@/mocks/categoryMocks';
import { navLinks } from '@/mocks/homeMocks';

type CategorySlug = 'game-assets' | 'sound-effects' | 'music' | 'visual-assets';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState<CategorySlug>((slug as CategorySlug) || 'game-assets');
  const [activeSubTab, setActiveSubTab] = useState('all');
  const [sortBy, setSortBy] = useState('popular');

  useEffect(() => {
    if (slug && ['game-assets', 'sound-effects', 'music', 'visual-assets'].includes(slug)) {
      setActiveSlug(slug as CategorySlug);
      setActiveSubTab('all');
    }
  }, [slug]);

  const info = categoryInfo[activeSlug];
  const items = categoryItems[activeSlug] || [];

  const subTabs = ['全部', '最热', '最新', '免费', '推荐'];
  const subTabKeys = ['all', 'hot', 'new', 'free', 'recommended'];

  const sortedItems = [...items].sort((a, b) => {
    if (sortBy === 'popular') return b.downloads - a.downloads;
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'size') return parseFloat(b.size) - parseFloat(a.size);
    return 0;
  });

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${info.name} - aigei`,
    description: info.description,
    url: `https://www.aigei.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'aigei',
      url: 'https://www.aigei.com',
    },
  };

  return (
    <>
      <SEOHead
        title={`${info.name}`}
        description={info.description}
        keywords={`aigei,aigei${info.name},aigei免费资源,${info.name}下载,游戏${info.name}`}
        canonicalUrl={`/category/${activeSlug}`}
        schema={categorySchema}
      />

      <main>
        <div className="relative h-[280px] md:h-[360px] overflow-hidden">
          <img src={info.banner} alt={info.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-4 md:px-6 pb-8 md:pb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                  <i className={`${info.icon} text-xl md:text-2xl text-white`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">{info.name}</h1>
              </div>
              <p className="text-sm md:text-base text-white/80 max-w-xl">{info.description}</p>
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="py-3">
              <Breadcrumb items={[
                { label: '首页', href: '/' },
                { label: info.name },
              ]} />
            </div>
            <div className="flex items-center gap-1 md:gap-2 pb-3 overflow-x-auto flex-nowrap">
              {navLinks.filter(l => l.href.startsWith('/category')).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeSlug === link.href.replace('/category/', '')
                      ? 'bg-primary-500 text-white font-medium'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto flex-nowrap">
              {subTabs.map((tab, i) => (
                <button
                  key={i}
                  onClick={() => setActiveSubTab(subTabKeys[i])}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeSubTab === subTabKeys[i]
                      ? 'bg-accent-100 text-accent-700 font-medium'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {tab}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm bg-transparent text-foreground-600 border border-background-300 rounded-lg px-3 py-1.5 cursor-pointer outline-none focus:border-primary-300"
                >
                  <option value="popular">最受欢迎</option>
                  <option value="rating">最高评分</option>
                  <option value="size">文件大小</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {sortedItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/category/${activeSlug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-100 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                        {item.format}
                      </span>
                      <span className="text-xs text-foreground-400">{item.size}</span>
                    </div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-xs text-secondary-500"></i>
                        <span className="text-xs text-foreground-600">{item.rating}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-foreground-400">
                        <i className="ri-download-line"></i>
                        <span>{item.downloads.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {sortedItems.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无素材</p>
              </div>
            )}

            <div className="mt-10 flex justify-center">
              <div className="flex items-center gap-1">
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-white text-sm font-medium cursor-pointer">1</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 text-sm cursor-pointer">2</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 text-sm cursor-pointer">3</button>
                <span className="px-1 text-foreground-400 text-sm">...</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 text-sm cursor-pointer">12</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 cursor-pointer">
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}